import { FileText, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { useAuth } from "../../contexts/AuthContext";

interface CertificateEnrollment {
  _id: string;
  course: {
    title: string;
    thumbnail?: string;
  };
  status: string;
  completionDate?: string;
  certificate?: {
    isIssued: boolean;
  };
}

interface CertificatesTableProps {
  enrollments: CertificateEnrollment[];
  onIssueCertificate: (enrollmentId: string) => void;
}

export function CertificatesTable({
  enrollments,
  onIssueCertificate,
}: CertificatesTableProps) {
  const { hasPermission } = useAuth();

  const canIssueCertificate = hasPermission('certificates:approve');

  // Only show completed enrollments or those with issued certificates
  const certificateData = enrollments.filter(
    (e) => e.status === "completed" || e.certificate?.isIssued,
  );

  return (
    <div className="space-y-4">
      <Card className="p-0 border shadow-sm">
        <div className="p-4 sm:p-6 pb-2">
          <h3 className="text-xl font-bold text-gray-900">
            Certificate Section
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Course Name
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Completion Date
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {certificateData.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    No certificates or completed courses found.
                  </td>
                </tr>
              ) : (
                certificateData.map((item) => (
                  <tr
                    key={item._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-3 sm:px-6 py-4 font-medium text-[#102040]">
                      {item.course?.title}
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-green-100 text-green-800">
                        Completed
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {item.completionDate
                        ? new Date(item.completionDate).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right">
                      {item.certificate?.isIssued ? (
                        <div className="flex items-center justify-end text-green-600 font-medium text-xs">
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Certificate Done
                        </div>
                      ) : canIssueCertificate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2 h-8 px-2"
                          onClick={() => onIssueCertificate(item._id)}
                        >
                          <FileText className="h-4 w-4" />
                          Issue Certificate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
